import React, { useState } from 'react';
import classNames from 'classnames';
import { IMSubscriptionPlan } from "@/types";
import { priceView } from '@/utils/utils';
import { pluralize } from '@/utils/pluralize';

interface PlanCardProps {
	plan: IMSubscriptionPlan;
	selectedPlanId?: string | null;
	getMonthlyEquivalent: (yearlyPlan: IMSubscriptionPlan) => IMSubscriptionPlan | null;
	expandable?: boolean;
	className?: string;
    discountPercent?: number;
}

interface CheckIconProps {
	className?: string
}

const CheckIcon: React.FC<CheckIconProps> = ({ className = '' }) => {
	return (
		<svg
			width="16"
			height="16"
			viewBox="0 0 16 16"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			className={`min-w-3 xl:min-w-4 w-3 xl:w-4 h-3 xl:h-4 mt-0.5 xl:mt-px ${className}`}
		>
			<circle cx="8" cy="8" r="8" fill="currentColor" />
			<path d="M5 7.65517L7.21739 10L11 6"
			      stroke="white"
			      strokeWidth="1.5"
			      strokeLinecap="round"
			      strokeLinejoin="round" />
		</svg>
	);
};

const PlanCard: React.FC<PlanCardProps> = ({
  plan,
  selectedPlanId,
  getMonthlyEquivalent,
  expandable = true,
  className = '',
  discountPercent,
}) => {
	const [expanded, setExpanded] = useState<boolean>(false);
	const isSelected = selectedPlanId === plan.id;

	const toggleExpanded = (e: React.MouseEvent) => {
		if (!expandable) return;
		e.stopPropagation();
		setExpanded(prev => !prev);
	};

	return (
		<div
			className={classNames(
				'select-none',
				className
			)}
			// onClick={handleClick}
		>
			<div className="flex items-center md:flex-row-reverse md:justify-between md:items-start">
				<div className="absolute right-4 xl:right-6 top-4 xl:top-6 w-6 md:w-5 xl:w-6 min-w-6 md:min-w-5 xl:min-w-6 h-6 md:h-5 xl:h-6 rounded-full border-[1.5px] flex items-center justify-center transition duration-300 ease-out group-hover:border-brand-green border-brand-green/[0.20]">
					<div className={classNames(
						'w-2.5 h-2.5 rounded-full bg-brand-green transition duration-300 ease-out',
						isSelected ? 'scale-1' : 'scale-0',
					)} />
				</div>
				<div className="flex flex-col items-start w-full">
					<div className="font-founders text-[28px]/[0.9] md:text-2xl/[0.7] xl:text-3xl/[0.9] mb-2.5 md:mb-3">{plan.name}</div>
					<div className="text-[11px]/[1] md:text-[13px] xl:text-base mb-5 md:mb-6 xl:mb-8">
						{plan.salesLimit === -1 ? 'Unlimited sales' : `Sales up to ${priceView(plan.salesLimit, 'USD').replace(/\.00$/, '')}/mo`}
					</div>
				</div>
			</div>
			<div className="flex flex-col gap-2.5 mb-[24px] md:mb-8 xl:mb-10">
				<div className="flex items-baseline">
					{plan.period === 'year' ? (
						<>
                            {discountPercent ? (
                                <>
                                    <span className="font-founders text-[34px]/[0.8] md:text-3xl xl:text-4xl font-semibold mr-2.5 text-[#BDBAB4] line-through">{priceView(getMonthlyEquivalent(plan)?.flatFee || 0, 'USD')}</span>
                                    <span className="font-founders text-[34px]/[0.8] md:text-3xl xl:text-4xl font-semibold">{priceView(plan.flatFee / 12 / 100 * (100 - discountPercent), 'USD')}</span>
                                </>
                            ) : (
							    <span className="font-founders text-[34px]/[0.8] md:text-3xl xl:text-4xl font-semibold">{priceView(plan.flatFee / 12, 'USD')}</span>
                            )}
							&nbsp;
							<span className="text-[13px]/[1.2] md:text-base">/ mo</span>
						</>
					) : (
						<>
                            {discountPercent ? (
                                <>
                                    <span className="font-founders text-[34px]/[0.8] md:text-3xl xl:text-4xl font-semibold mr-2.5 text-[#BDBAB4] line-through">{priceView(plan.flatFee, 'USD')}</span>
                                    <span className="font-founders text-[34px]/[0.8] md:text-3xl xl:text-4xl font-semibold">{priceView(plan.flatFee / 100 * (100 - discountPercent), 'USD')}</span>
                                </>
                            ) : (
                                <span className="font-founders text-[34px]/[0.8] md:text-3xl xl:text-4xl font-semibold">{priceView(plan.flatFee, 'USD')}</span>
                            )}
							&nbsp;
							<span className="text-[13px]/[1.2] md:text-base">/ {plan.period.toLowerCase() === 'month' ? 'mo' : 'yr'}</span>
						</>
					)}
				</div>
				{plan.period === 'year' && getMonthlyEquivalent(plan) && (
					<div className="text-[14px]/[1.3] font-medium flex gap-1">
						Billed as: 
                        {discountPercent ? (
                            <>
                                <span className="text-[#A5A5A5] line-through">{priceView((getMonthlyEquivalent(plan)?.flatFee || 0) * 12, 'USD')}</span> <span>{priceView(plan.flatFee * (100 - discountPercent) / 100, 'USD')}/yr</span>
                            </>
                        ) : (
                            <>
                                <span className="text-[#A5A5A5] line-through">{priceView((getMonthlyEquivalent(plan)?.flatFee || 0) * 12, 'USD')}</span> <span>{priceView(plan.flatFee, 'USD')}/yr</span>
                            </>
                        )}
					</div>
				)}
			</div>

			<div>
				{expandable && (
					<div className="flex items-center md:hidden lg:mb-4">
            <span className="text-[13px]/[1.2] font-medium mb-1 pr-1 cursor-pointer"
                  onClick={toggleExpanded}>What is included in the plan</span>
						<svg className={`w-4 h-4 transition-transform duration-300 -translate-y-px cursor-pointer ${expanded ? 'rotate-180' : ''}`}
						     onClick={toggleExpanded}
						     width="16"
						     height="16"
						     viewBox="0 0 16 16"
						     fill="none"
						     xmlns="http://www.w3.org/2000/svg">
							<path d="M4 6L8 10L12 6"
							      stroke="currentColor"
							      strokeWidth="1.4"
							      strokeMiterlimit="10"
							      strokeLinecap="round" />
						</svg>
					</div>
				)}

				<div className={`md:block text-[13px]/[1.2] xl:text-base mt-4 xl:mt-0 mb-2 xl:mb-0 ${(expanded || !expandable) ? 'block' : 'hidden'}`}>
					<div className="flex gap-2 xl:gap-4 mb-3 last:mb-0">
						<CheckIcon className="min-w-3 xl:min-w-4 w-3 xl:w-4 h-3 xl:h-4 mt-0.5 xl:mt-px" />
						<span>All Payment Integrations</span>
					</div>
					<div className="flex gap-2 xl:gap-4 mb-3 last:mb-0">
						<CheckIcon className="min-w-3 xl:min-w-4 w-3 xl:w-4 h-3 xl:h-4 mt-0.5 xl:mt-px" />
						<span>Membership Page</span>
					</div>
					<div className="flex gap-2 xl:gap-4 mb-3 last:mb-0">
						<CheckIcon className="min-w-3 xl:min-w-4 w-3 xl:w-4 h-3 xl:h-4 mt-0.5 xl:mt-px" />
						<span>Membership Bot</span>
					</div>
					<div className="flex gap-2 xl:gap-4 mb-3 last:mb-0">
						<CheckIcon className="min-w-3 xl:min-w-4 w-3 xl:w-4 h-3 xl:h-4 mt-0.5 xl:mt-px" />
						<span>{pluralize(plan.maxProjectsCount, 'project')}</span>
					</div>
					<div className="flex gap-2 xl:gap-4 mb-3 last:mb-0">
						<CheckIcon className="min-w-3 xl:min-w-4 w-3 xl:w-4 h-3 xl:h-4 mt-0.5 xl:mt-px" />
						<span>{plan.maxAccessCodes === -1 ? 'Unlimited access codes' : pluralize(plan.maxAccessCodes, 'access code')}</span>
					</div>
					{/*{plan.canUseUTMLinks && (*/}
					{/*	<div className="flex gap-2 xl:gap-4 mb-3 last:mb-0">*/}
					{/*		<CheckIcon className="min-w-3 xl:min-w-4 w-3 xl:w-4 h-3 xl:h-4 mt-0.5 xl:mt-px" />*/}
					{/*		<span>UTM-links</span>*/}
					{/*	</div>*/}
					{/*)}*/}
					{plan.canUseCustomHtmlCss && (
						<div className="flex gap-2 xl:gap-4 mb-3 last:mb-0">
							<CheckIcon className="min-w-3 xl:min-w-4 w-3 xl:w-4 h-3 xl:h-4 mt-0.5 xl:mt-px" />
							<span>Google Analytics</span>
						</div>
					)}
					{plan.canUseAffiliateProgram && (
						<div className="flex gap-2 xl:gap-4 mb-3 last:mb-0">
							<CheckIcon className="min-w-3 xl:min-w-4 w-3 xl:w-4 h-3 xl:h-4 mt-0.5 xl:mt-px" />
							<span>Affiliate program</span>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default PlanCard;
